<script setup>
import { usePlayerWalkScreen } from './usePlayerWalkScreen'
import { firstSound } from '../playback/firstSoundSignal'
import { ref, provide, onMounted, onUnmounted, onActivated, onDeactivated, computed, inject, watch, defineAsyncComponent, nextTick } from 'vue'
import { useSocialRowsGate } from '../composables/useSocialDoor'
import { useRouter, useRoute } from 'vue-router'

// Global backdrop
import SumiEBackground from '@/components/SumiEBackground.vue'

// Screen components
import LearningPlayer from '@/components/LearningPlayer.vue'
import BottomNav from '@/components/BottomNav.vue'
import PlayerRestingState from '@/components/PlayerRestingState.vue'
import SpeakingRoomChip from '@/components/SpeakingRoomChip.vue'
import SetPasswordNudge from '@/components/auth/SetPasswordNudge.vue'
import { RELEASE_HOLDS } from '@/config/releaseHolds'
// CourseSelector is lazy and mounts late (job #300). It used to be static and
// always mounted, closed: its chunk sat on the first-sound path, and mounting
// it fired a running-order read for every enrolled course plus a round-index
// read, all competing with the first sound's own requests. It now mounts the
// moment something opens it, or once the first sound has played (so its belt
// dots are ready for the first tap). First-visit course choice is the /start
// picker (FirstPickerView), not this modal.
const loadCourseSelector = () => import('@/components/CourseSelector.vue')
const CourseSelector = defineAsyncComponent(loadCourseSelector)

// Modals async — pre-warmed in onMounted (see below) so chunks are
// usually already loaded by the time the user clicks. Combined with
// the min-height on .settings-panel, the panel-collapse issue from
// the previous attempt can't recur even if a user clicks before
// pre-warm completes.
const SettingsScreen = defineAsyncComponent(() => import('@/components/SettingsScreen.vue'))
const CourseExplorer = defineAsyncComponent(() => import('@/components/CourseExplorer.vue'))
const BrowseScreen = defineAsyncComponent(() => import('@/components/BrowseScreen.vue'))
const SignInModal = defineAsyncComponent(() => import('@/components/auth/SignInModal.vue'))

// Global auth modal state (shared singleton)
import { useAuthModal } from '@/composables/useAuthModal'
import { getSharedBeltProgress, getSeedFromLegoId } from '@/composables/useBeltProgress'
import { useSharedUserEntitlements } from '@/composables/useUserEntitlements'
import { useCheckout } from '@/composables/useCheckout'
import { useSectorThread } from '@/composables/useSectorThread'
import { courseToFallBackTo } from '@/containers/scopedPickerClose'
import SectorPicker from '@/components/SectorPicker.vue'
import ModeCover from '@/components/ModeCover.vue'
import { takePlayerIntent } from '@/nav/playerIntent'
import { createModeIntentWait } from '@/nav/modeIntentWait'
import { useSheetsYieldToMode } from '@/nav/sheetsYieldToMode'
import { routeModeOf, modePath, routeStepFor } from '@/nav/modeRoute'
import { deferredPending } from '@/walkthrough/walkState'
import { viewerPersona } from '@/walkthrough/persona'
import { useUserRole } from '@/composables/useUserRole'
import { installedSignInPending, returnWhenRoleKnown } from '@/platform/installedReturn'
import { coursePickerRequested } from '@/platform/learnLink'
import { shouldShowModeSwitchReminder, readModeSwitchReminderSeen, markModeSwitchReminderSeen } from '@/composables/modeSwitchReminder'

// Inject from App
const supabaseClient = inject('supabase')
const progressStore = inject('progressStore')
const sessionStore = inject('sessionStore')
const courseDataProvider = inject('courseDataProvider')
const auth = inject('auth')
const config = inject('config')
const themeContext = inject('theme', null)
const router = useRouter()
const route = useRoute()

// When launched "Play as class" from the tutor (teach) shell, the player runs
// INSIDE TeachContainer, which keeps its TopNav fixed at the top. The player's
// root (.learning-player-root) and all its pop-ups are `position: fixed; inset: 0`,
// so they anchor to the viewport — sliding their top controls UNDER the teach nav.
// On this route we make .player-container a containing block (via transform) and
// size it to the area below the nav, so every fixed descendant anchors below the
// nav instead of the viewport top. Standalone player (route '/') is untouched.
// True when the player runs inside a shell that already provides its own white
// (Mist) top nav: the tutor "Play as class" (teach-play) AND the schools
// "Play as class" (schools-play). In both cases the player's own legacy dark
// `.class-bar` ("Back to classes") is a redundant second top bar in the wrong
// theme — pass embedded so it's hidden and the shell's white nav is the single
// source of navigation.
const isTeachEmbedded = computed(
  () => route.name === 'teach-play' || route.name === 'schools-play',
)

// Global auth modal (shared with BottomNav and other components)
const {
  isOpen: isAuthOpen,
  open: openAuth,
  close: closeAuth,
} = useAuthModal()
// Aliases for backwards compatibility within this file
const closeAuthModals = closeAuth

// Navigation state — 2 screens + overlays
// Screens: 'progress' | 'player'
// Overlays: showLibrary, showSettings, showExplorer
const currentScreen = ref('player')
const selectedCourse = ref(null)
const isLearning = ref(false)

// Overlay state (not screens)
const showSettings = ref(false)
const showLibrary = ref(false)

// A Show me placed on the player's own screen (the mode tray, the resting
// screen's course name) — deferred by the Handbook, claimed here once no
// overlay covers the player. The engine is imported only when a walk is
// actually waiting, so the pack stays off the boot path (job #850).
const { platformRole: walkPlatformRole, effectiveEducationalRole: walkEducationalRole } = useUserRole()
const showExplorer = ref(false)
usePlayerWalkScreen(route, showSettings, showLibrary, showExplorer)
watch([deferredPending, () => route.fullPath, showSettings, showLibrary], async () => {
  if (!deferredPending.value || route.name !== 'player') return
  const m = await import('@/walkthrough/useWalkthrough')
  await nextTick()
  if (route.name !== 'player') return
  const persona = viewerPersona(walkPlatformRole.value, walkEducationalRole.value)
  if (showSettings.value) m.claimDeferredWalk(persona, 'player-settings')
  else if (showLibrary.value) m.claimDeferredWalk(persona, 'library', auth?.isGuest?.value ? 'guest' : 'signed-in')
  else m.claimDeferredWalk(persona, 'player')
}, { immediate: true, flush: 'post' })
const showCourseSelector = ref(false)
// Whether the CourseSelector is in the tree yet, and the open state handed to
// it. A selector asked for before it has mounted is mounted closed first and
// opened a tick later, so its slide-up transition still plays.
const courseSelectorMounted = ref(false)
const courseSelectorOpen = ref(false)
const mountCourseSelector = async () => {
  if (courseSelectorMounted.value) return
  courseSelectorMounted.value = true
  await loadCourseSelector().catch(() => {})
  await nextTick()
}
watch(showCourseSelector, async (open) => {
  if (!open) { courseSelectorOpen.value = false; return }
  await mountCourseSelector()
  courseSelectorOpen.value = showCourseSelector.value
})
// Course codes the picker is restricted to, when something opened it scoped
// (org enrolment). Empty = the whole catalogue, the ordinary case.
const courseSelectorOnly = ref([])
// A course link naming a course the catalogue does not hold opens the picker
// (platform/learnLink). Watched, not read once: the catalogue can answer
// before or after this container mounts.
watch(coursePickerRequested, (asked) => {
  if (!asked) return
  coursePickerRequested.value = false
  showCourseSelector.value = true
}, { immediate: true })

const learningPlayerRef = ref(null)

// Player state - shared with nav bar for play/stop button.
// PULLED from the player's derived isAudioPlaying (cycle engine OR
// welcome/intro/pod/commentary OR the preparing window), not mirrored from
// an event. The old @playStateChanged mirror was edge-triggered: a missed
// or reordered emission left the transport showing PLAY while audio was
// audibly mid-cycle until the next engine toggle re-synced it (Jonathan's
// staging report, 2026-07-28). A computed reads the current truth on every
// re-render — there is no edge to miss.
const isPlaying = computed(() => learningPlayerRef.value?.isAudioPlaying ?? false)

// Mode overlay state (overlays live inside LearningPlayer; BottomNav needs
// them here). PULLED via the template ref like isPlaying above — the old
// @listeningModeChanged / @pronunciationModeChanged event mirrors required
// every overlay write to remember its paired emit; a missed pairing left
// BottomNav's mode button lying about the overlay. A computed reads the
// overlay's current truth instead.
const isListeningMode = computed(() => learningPlayerRef.value?.isListeningMode ?? false)

// Driving mode state (tracked for BottomNav return arrow)

const isPronunciationMode = computed(() => learningPlayerRef.value?.isPronunciationMode ?? false)
// SCRIPT mode (job #453) — same pull-consistency shape as the two above.
const isScriptPracticeMode = computed(() => learningPlayerRef.value?.isScriptPracticeMode ?? false)
const playerHasScriptPractice = computed(() => learningPlayerRef.value?.hasScriptPractice ?? false)

// Mode button visibility (controlled by Settings, stored in localStorage)
const showListeningBtn = ref(false)
const showPronunciationBtn = ref(false)


// Script mode (romanisation guide on/off) is controlled on the player screen
// itself since 2026-09-22 — the tray no longer carries a remote control for it.
const isPlayerReady = computed(() => !(learningPlayerRef.value?.isAwakening ?? true))
// The PLAY affordance's own readiness (Tom, 2026-08-08). isPlayerReady means
// "the lesson exists"; it does not mean a tap makes a sound, and cold on Fast
// 3G those were seven seconds apart. The bottom-nav button waits for the first
// clip's bytes too, so when it stops flashing it is telling the truth. The
// resting overlay (belt badge, Easy/Fast) keeps the plain flag — it v-if's on
// it, so it is honest already: absent rather than fake.
const isPlayButtonReady = computed(
  () => isPlayerReady.value && (learningPlayerRef.value?.isFirstClipReady ?? false),
)

// Class context (when launched from Schools)
const classContext = ref(null)

// Preview mode: skip to a specific LEGO index via URL param ?preview=50
const previewLegoIndex = computed(() => {
  if (typeof window === 'undefined') return 0
  const params = new URLSearchParams(window.location.search)
  const preview = params.get('preview')
  return preview ? parseInt(preview, 10) || 0 : 0
})

// Active course and enrolled courses state
const activeCourse = inject('activeCourse')
const enrolledCourses = inject('enrolledCourses')
const handleCourseSelect = inject('handleCourseSelect')

// Navigation functions
const navigate = (screen, data = null) => {
  // Close any open auth modals when navigating
  closeAuthModals()

  // Let audio continue when browsing library/progress — center button shows Stop from any screen

  // CRITICAL: Unlock audio element synchronously within user gesture context.
  if (screen === 'player' && learningPlayerRef.value?.unlockAudio) {
    learningPlayerRef.value.unlockAudio()
  }

  if (data) {
    selectedCourse.value = data
  }
  currentScreen.value = screen
  isLearning.value = false
}

const goHome = () => navigate('player')
const startLearning = (course) => navigate('player', course)

// Handle nav events
const handleNavigation = (screen) => {
  // Always close course selector when navigating
  showCourseSelector.value = false

  if (screen === 'library') {
    toggleLibrary()
    return
  }
  // Close library overlay when navigating elsewhere
  showLibrary.value = false
  navigate(screen)
}

const handleStartLearning = () => {
  startLearning(activeCourse.value || selectedCourse.value)
}

// Handle play/stop toggle from nav bar
const handleTogglePlayback = () => {
  if (learningPlayerRef.value) {
    learningPlayerRef.value.togglePlayback()
  }
}

// Bottom-nav transport now steps the LEGO axis (was CYCLE — cycle moved to the
// phase pill, belt moved to the header). Tom 2026-06-01.
// In listening mode the same buttons step the overlay's active sentence
// back/forward instead (Tom 2026-07-04) — listeningStep returns true when
// the overlay handled it.
const handleRevisit = () => {
  if (learningPlayerRef.value?.listeningStep?.(-1)) return
  if (learningPlayerRef.value?.handleRoundBack) {
    learningPlayerRef.value.handleRoundBack()
  }
}

const handleSkip = () => {
  if (learningPlayerRef.value?.listeningStep?.(1)) return
  if (learningPlayerRef.value?.handleRoundForward) {
    learningPlayerRef.value.handleRoundForward()
  }
}


// (Play state is pulled from the player ref — see the isPlaying computed
// above. The old @playStateChanged event mirror is gone by design.)

// (Listening/pronunciation mode state is pulled from the player ref — the
// old @listeningModeChanged / @pronunciationModeChanged event mirrors are
// gone by design, same as play state.)

// Handle exit listening mode from BottomNav (user navigated away)
const handleExitListeningMode = () => {
  if (learningPlayerRef.value) {
    learningPlayerRef.value.exitListeningMode()
  }
}

// The bar's back arrow in Listening: one level up inside the overlay, and
// out of Listening only from the top (job #48).
const handleBackListening = () => {
  const p = learningPlayerRef.value
  if (p?.listeningBack) p.listeningBack()
  else p?.exitListeningMode?.()
}

// Handle mode toggle from BottomNav mode buttons
const handleToggleListening = () => {
  dropPendingIntent()
  if (learningPlayerRef.value?.handleListeningToggle) {
    learningPlayerRef.value.handleListeningToggle()
  }
}

// Handle exit pronunciation mode from BottomNav
const handleExitPronunciationMode = () => {
  if (learningPlayerRef.value) {
    learningPlayerRef.value.exitPronunciationMode()
  }
}

const handleTogglePronunciation = () => {
  if (learningPlayerRef.value?.handlePronunciationToggle) {
    learningPlayerRef.value.handlePronunciationToggle()
  }
}

const handleToggleScriptPractice = () => {
  dropPendingIntent()
  learningPlayerRef.value?.handleScriptPracticeToggle?.()
}

// Handle exit SCRIPT mode from BottomNav (back arrow, or navigating away)
const handleExitScriptPracticeMode = () => {
  learningPlayerRef.value?.exitScriptPracticeMode?.()
}

const handleSetLearningMode = (mode) => {
  learningPlayerRef.value?.setLearningMode?.(mode)
}

// The one-time "switch any time, lose nothing" line under the Easy / Fast
// switch (Tom, 2026-09-28). Rules in composables/modeSwitchReminder.ts; the
// resting screen reports when the visit that showed it ends.
const modeSwitchReminderSeen = ref(readModeSwitchReminderSeen())
const showModeSwitchReminder = computed(() => shouldShowModeSwitchReminder({
  seen: modeSwitchReminderSeen.value,
  playerReady: !!isPlayerReady.value,
  roundsFinished: learningPlayerRef.value?.roundsFinishedThisSession ?? 0,
  courseSeed: completedSeeds.value,
  inClass: !!classContext.value,
}))
const handleModeSwitchReminderSeen = () => {
  modeSwitchReminderSeen.value = true
  markModeSwitchReminderSeen()
}

const handleToggleOffline = () => {
  if (learningPlayerRef.value?.toggleOffline) {
    learningPlayerRef.value.toggleOffline()
  }
}

// Speaking, on the mode selector (Tom, 2026-09-24): the main course. Leave
// whichever mode owns the screen through that mode's own existing exit — the
// same toggles the old on/off rows used — and the main player is what is left.
const handleSpeaking = () => {
  dropPendingIntent()
  if (isScriptPracticeMode.value) handleToggleScriptPractice()
  else if (isListeningMode.value) handleToggleListening()
  else if (isPronunciationMode.value) handleExitPronunciationMode()
}

// Offline and Specialist live on the course in the Library (job #199; they
// were in Settings from 2026-09-24). Switching Offline ON opens the full-screen
// depth picker and the Specialist picker opens its own, so the Library closes
// first: one popup at a time. Offline OFF opens nothing, so the learner stays.
const handleLibraryOffline = () => {
  if (!(learningPlayerRef.value?.offlineActive ?? false)) closeLibrary()
  handleToggleOffline()
}
const handleLibraryOpenSector = () => {
  closeLibrary()
  void openSectorPicker(sectorCourseCodeForPicker.value)
}

// Connect (job #488): the social layer at /connect is a full page, not an
// in-session mode, so the tray row navigates rather than toggling. The page
// itself already handles eligibility — this only hides the row from guests,
// who have no identity for anyone to connect with. School accounts see neither
// row (job #113): the server's answer, via useSocialRowsGate.
const showConnect = useSocialRowsGate(computed(() => !!auth?.isAuthenticated?.value && !auth?.isGuest?.value))
// Talk to humans (plate A-373): the tray's one social door, into /talk.
// /connect, /groups, /games and the parked /rooms stay live behind it or by URL.
const handleTalk = () => {
  router.push('/talk')
}
// Study groups: its own tray row, directly below Talk to humans (2026-09-24).
const handleStudyGroups = () => {
  router.push('/groups')
}

// Handle starting at a specific seed from CourseBrowser
const handleStartAtSeed = (seedNumber) => {
  closeLibrary()
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('ssi-jump-to-seed', {
      detail: { seedNumber },
    }))
  }, 100)
}

// Library → Listening's All (job #754): the Library shows the set, this plays it.
const handleOpenListeningAll = () => {
  closeLibrary()
  learningPlayerRef.value?.openListeningAll?.()
}

// Library → My learning journey so far (job #857).
const handleOpenJourney = () => {
  closeLibrary()
  learningPlayerRef.value?.openJourney?.()
}

// Library overlay
const toggleLibrary = async () => {
  const opening = !showLibrary.value
  if (opening) {
    showSettings.value = false // Close settings if open
    // stopEverything(), not the transport pause: opening an overlay halts the
    // audio but it is NOT a pause tap, and logging it as one is what made
    // tap_pause unreadable (job #219/#223).
    if (learningPlayerRef.value?.stopEverything) {
      learningPlayerRef.value.stopEverything()
    }
  }
  showLibrary.value = !showLibrary.value

  // Opening the Library is a READ of the activity tiles, so land the in-flight
  // telemetry deltas first and only then refetch — otherwise "Phrases spoken"
  // shows the last-flushed snapshot and looks stuck one session behind.
  // Same ordering as handleBeltPillTap does for the progress modal.
  if (opening && adaptationConsented.value) {
    try {
      await learningPlayerRef.value?.flushTelemetryDelta?.()
    } catch { /* flush is best-effort */ }
    void loadPhrasesSpoken()
  }
}

const closeLibrary = () => {
  showLibrary.value = false
}

// Settings overlay
const toggleSettings = () => {
  if (!showSettings.value) {
    showLibrary.value = false // Close library if open
    showCourseSelector.value = false // Close course selector if open
    // stopEverything(), not the transport pause: opening an overlay halts the
    // audio but it is NOT a pause tap, and logging it as one is what made
    // tap_pause unreadable (job #219/#223).
    if (learningPlayerRef.value?.stopEverything) {
      learningPlayerRef.value.stopEverything()
    }
  }
  showSettings.value = !showSettings.value
}

const closeSettings = () => {
  showSettings.value = false
  settingsOpenPassword.value = false
}

// The set-a-password banner's button: Settings, opened on its Set Password form.
const settingsOpenPassword = ref(false)
const openSettingsOnPassword = () => {
  settingsOpenPassword.value = true
  if (!showSettings.value) toggleSettings()
}

const openExplorerOverlay = () => {
  showExplorer.value = true
}

const closeExplorerOverlay = () => {
  showExplorer.value = false
}

// Real learner progress from shared belt progress (created by LearningPlayer)
const beltProgress = computed(() => getSharedBeltProgress())

// ── The sector thread — the helix's second strand ──────────────────────────
// The mode tray owns the row, SectorPicker owns the two steps, useSectorThread
// owns the data; this is the seam between them, and it lives here because this
// is where BottomNav is mounted and where the current course code is known.
// Nothing here schedules anything: choosing a walk writes the thread row, and
// the merge scheduler picks it up when a segment has content to interleave.
const sectorCourseCodeForPicker = computed(
  () => activeCourse.value?.course_code || selectedCourse.value?.course_code || ''
)
const showSectorPicker = ref(false)
const {
  sectors: sectorOptions,
  loadingSectors,
  sectorsError,
  activeThread: activeSectorThread,
  loadSectors,
  loadThreads,
  chooseSector,
  setThreadActive,
} = useSectorThread()

// The tray's desc line: the walk and role in words, never a code.
const sectorDesc = computed(() => {
  const thread = activeSectorThread.value
  if (!thread) return ''
  const walk = sectorOptions.value.find(s => s.sectorCourseCode === thread.sectorCourseCode)
  const name = walk?.slug ? walk.slug.charAt(0).toUpperCase() + walk.slug.slice(1) : ''
  return name ? `${name} — ${thread.role}` : thread.role
})

const openSectorPicker = async (courseCode) => {
  showSectorPicker.value = true
  const code = courseCode || sectorCourseCodeForPicker.value
  if (!code) return
  await Promise.all([loadSectors(code), loadThreads(code)])
}

const handleSectorChoose = async (choice) => {
  const code = sectorCourseCodeForPicker.value
  if (!code) return
  try {
    await chooseSector(code, choice.sectorCourseCode, choice.role)
    showSectorPicker.value = false
  } catch (err) {
    // Loud, never swallowed: the picker keeps its own error line.
    console.error('[sector] choose failed', err)
  }
}

// The tray toggle parks or resumes the chosen walk. Parking is never
// destructive — the thread's cursor stays exactly where it was.
const handleSectorToggle = async () => {
  const thread = activeSectorThread.value
  const code = sectorCourseCodeForPicker.value
  if (!thread || !code) return
  try {
    await setThreadActive(code, thread.sectorCourseCode, !thread.active)
  } catch (err) {
    console.error('[sector] toggle failed', err)
  }
}

// Belt CSS vars for cascading to BottomNav and other siblings
const containerBeltVars = computed(() => {
  const bp = beltProgress.value
  if (!bp) return {}
  return bp.beltCssVars.value
})

// Seed count derived from highestLegoId
const completedSeeds = computed(() => {
  const bp = beltProgress.value
  if (!bp) return 0
  // Journey position (job #949: a woven course's running order), which is
  // getSeedFromLegoId for every course without one.
  const seed = (bp.journeySeedOf ?? getSeedFromLegoId)(bp.highestLegoId.value)
  return seed ?? 0
})

// Belt = the LEGO the current ROUND introduced = one position, one belt. Use
// playingBelt (the live cursor position), NOT currentBelt — currentBelt is
// max(position, highest), a ratchet to the highest belt ever reached, which
// the model says must NEVER be displayed (highest is a separate "you've been
// as far as" readout). When you jump DOWN a belt, this must follow you down.
// Fixed the resting pill + background tint showing Purple while playing Orange.
const currentBeltName = computed(() => {
  const bp = beltProgress.value
  if (!bp) return 'white'
  return bp.playingBelt.value.name
})

const currentBeltColor = computed(() => {
  const bp = beltProgress.value
  if (!bp) return '#ffffff'
  return bp.playingBelt.value.color
})

// Total seeds in course (dynamic per course)
const totalSeeds = computed(() => {
  const bp = beltProgress.value
  return bp?.courseSeedCount?.value ?? bp?.TOTAL_SEEDS ?? 668
})

// Usage stats. The headline "Total Time" is the learner's COMMITMENT metric, so
// it must be true time-in-app (session spans, all courses, incl. listening) — not
// the local per-course estimate, and never the old audio-playback-seconds number.
// For a signed-in learner we read the server value (same definition as admin);
// guests / offline fall back to the local session-history estimate.
// Also the encouragement-taper signal (owner ruling 2026-08-06): it is passed
// to LearningPlayer RAW — server value or null — deliberately WITHOUT the
// belt-progress fallback below, because that fallback is per-course and
// 30-day-windowed (useBeltProgress reads `ssi-session-history-<courseCode>`),
// so it is not a cumulative cross-course number. null there means "unknown",
// which the taper reads as "beginner".
const serverEngagedMinutes = ref(null)
// True when serverEngagedMinutes is a position-derived backup estimate (no
// session logs yet) rather than logged time — never set for the local
// belt-progress fallback, which is its own (unflagged) estimate.
const serverEngagedMinutesEstimated = ref(false)
async function loadEngagedMinutes() {
  const sb = supabaseClient
  if (!sb?.value) return
  try {
    const { data: { session } } = await sb.value.auth.getSession()
    const token = session?.access_token
    if (!token) return // guest — keep the local estimate
    const res = await fetch('/api/me/engaged-time', { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) return
    const data = await res.json()
    if (typeof data?.engagedMinutes === 'number') {
      serverEngagedMinutes.value = data.engagedMinutes
      serverEngagedMinutesEstimated.value = !!data.isEstimated
    }
  } catch {
    /* non-fatal — fall back to the local estimate */
  }
}
const totalLearningMinutes = computed(() =>
  serverEngagedMinutes.value ?? beltProgress.value?.totalLearningMinutes.value ?? 0
)
const totalLearningMinutesEstimated = computed(() =>
  serverEngagedMinutes.value != null && serverEngagedMinutesEstimated.value
)
// "Phrases spoken" — cycles where the VAD actually heard the learner, summed
// LIFETIME across EVERY course (owner ruling 2026-08-19, Activity-tiles
// diagnosis rec 3). Server-computed by /api/me/phrases-spoken off
// learner_speaking_opportunities.phrases_spoken.
//
// The local belt-progress value it replaces could not carry this: it read
// localStorage `ssi-session-history-<courseCode>`, filtered to the last 30
// days, for one course only, and was banked only when endSession() ran — so a
// session ended by closing the tab lost its count. The server ledger rides the
// existing delta/watermark flush, which already fires on visibilitychange and
// beforeunload.
//
// null = not loaded / unknown (guest, offline, error). The local value is the
// fallback until the server answers — it is a floor, never larger than the truth.
const serverPhrasesSpoken = ref(null)
async function loadPhrasesSpoken() {
  const sb = supabaseClient
  if (!sb?.value) return
  try {
    const { data: { session } } = await sb.value.auth.getSession()
    const token = session?.access_token
    if (!token) return // guest — nothing banked server-side
    const res = await fetch('/api/me/phrases-spoken', { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) return
    const data = await res.json()
    if (typeof data?.phrasesSpoken === 'number') serverPhrasesSpoken.value = data.phrasesSpoken
  } catch {
    /* non-fatal — fall back to the local 30-day, single-course count */
  }
}
const totalPhrasesSpoken = computed(() =>
  serverPhrasesSpoken.value ?? beltProgress.value?.totalPhrasesSpoken.value ?? 0
)

// "Phrases learnt" — DISTINCT LEGOs introduced, summed across EVERY course the
// learner is enrolled in (owner ruling 2026-08-19). Server-computed, because it
// needs each enrollment's cursor measured against that course's course_legos;
// the client only knows about the one course it currently has loaded.
// Replaces the old "Words" tile, which printed the seed number parsed out of a
// single course's resume cursor — a position readout wearing a count's label.
// null = not loaded / unknown (guest, offline, error); the tile renders 0 only
// when the server actually says 0.
const legosLearnt = ref(null)
async function loadLegosLearnt() {
  const sb = supabaseClient
  if (!sb?.value) return
  try {
    const { data: { session } } = await sb.value.auth.getSession()
    const token = session?.access_token
    if (!token) return // guest — no enrollments to sum
    const res = await fetch('/api/me/legos-learnt', { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) return
    const data = await res.json()
    if (typeof data?.legosLearnt === 'number') legosLearnt.value = data.legosLearnt
  } catch {
    /* non-fatal — the tile falls back to the local single-course count */
  }
}
// Until the server answers, show the one number we can honestly derive locally:
// the LEGOs introduced in the current course up to the cursor is not knowable
// client-side either, so fall back to 0 rather than to a seed position.
const phrasesLearnt = computed(() => legosLearnt.value ?? 0)

// ── Spoken-phrases tile visibility ──
// A category with no live data source is HIDDEN, not shown as 0 (owner ruling
// 2026-08-19): a 0 asserts "you have spoken nothing", when the truth is "we
// weren't listening". phrasesSpoken only ever increments while the adaptation
// (VAD/mic) engine is running, and that requires this consent — off by default.
// Mirrors ADAPTATION_CONSENT_KEY in LearningPlayer.vue.
const ADAPTATION_CONSENT_KEY = 'ssi-adaptation-consent'
const adaptationConsented = ref(false)
function loadAdaptationConsent() {
  adaptationConsented.value = localStorage.getItem(ADAPTATION_CONSENT_KEY) === 'true'
}
const showPhrasesSpokenTile = computed(() => adaptationConsented.value)

// Admin detection (mirrors SettingsScreen logic)
const ADMIN_EMAIL_DOMAINS = ['saysomethingin.com', 'ssi.cymru']
const ADMIN_EMAILS = ['tom@tomcassidy.co.uk']
const isAdmin = computed(() => {
  const email = (auth?.user?.value?.emailAddresses?.[0]?.emailAddress || '').toLowerCase()
  if (!email) return false
  if (ADMIN_EMAILS.some(e => email === e.toLowerCase())) return true
  const domain = email.split('@')[1]
  return ADMIN_EMAIL_DOMAINS.some(d => domain === d.toLowerCase())
})

// Handle auth success — refresh entitlements and course list so newly redeemed codes take effect
const fetchEnrolledCourses = inject('fetchEnrolledCourses', null)
let stopInstalledReturn = null

const handleAuthSuccess = async () => {
  console.debug('[PlayerContainer] Auth successful, refreshing entitlements...')
  closeAuth()
  // The installed app opened signed out (platform/installedReturn, job #129):
  // once the learner row says who this is, a school role goes to its school.
  // No deadline: on slow school wifi the role can take longer than any wait
  // we would pick (lane review #149). The flag clears when the role arrives.
  if (installedSignInPending.value && !stopInstalledReturn) {
    stopInstalledReturn = returnWhenRoleKnown(useUserRole(), (path) => router.push(path), () => { stopInstalledReturn = null })
  }
  // Refresh entitlements so paywall re-evaluates with new codes
  const { refresh: refreshEntitlements } = useSharedUserEntitlements()
  await refreshEntitlements().catch(() => {})
  // Re-fetch courses so newly accessible courses appear
  if (fetchEnrolledCourses) await fetchEnrolledCourses().catch(() => {})
  // If the user started the in-player paywall checkout while signed out, the
  // sign-in modal interrupted it — continue straight into Paddle now.
  await useCheckout().completePendingCheckout().catch(() => {})
}

// Check for class context from Schools
const checkClassContext = () => {
  const params = new URLSearchParams(window.location.search)
  const classId = params.get('class')

  if (classId) {
    const stored = sessionStorage.getItem('ssi-demo-active-class') || localStorage.getItem('ssi-active-class')
    if (stored) {
      try {
        classContext.value = JSON.parse(stored)
        console.debug('[PlayerContainer] Class context loaded:', classContext.value)
      } catch (e) {
        console.error('[PlayerContainer] Failed to parse class context:', e)
      }
    }
    return true
  }
  return false
}

// Clear class context
const clearClassContext = () => {
  classContext.value = null
  sessionStorage.removeItem('ssi-demo-active-class')
  localStorage.removeItem('ssi-active-class')
  const url = new URL(window.location.href)
  url.searchParams.delete('class')
  window.history.replaceState({}, '', url)
}

// Handle going home from player
const handleGoHome = () => {
  // The platform admin's own class session (job #601) hands back the admin's
  // OWN learner home with a full load, so the class's course — switched for
  // this tab only — gives way to whatever the admin's account resolves to.
  if (classContext.value?.adminLaunch) {
    clearClassContext()
    window.location.assign('/')
    return
  }
  if (classContext.value) {
    // Return to the teacher's own dashboard — the tutor dashboard (/teach)
    // or the schools dashboard — so the teacher keeps their context instead
    // of being dumped into the learner home (founder ruling, 2026-07-30:
    // ending a session hands the schools user back their dashboard).
    const launchedFromTeach = router.currentRoute.value.path.startsWith('/tutors/dashboard')
    clearClassContext()
    router.push(launchedFromTeach ? '/tutors/dashboard' : '/schools')
  } else {
    goHome()
  }
}

// Load mode button visibility from localStorage and listen for changes
const loadModeVisibility = () => {
  showListeningBtn.value = localStorage.getItem('ssi-mode-listening') === 'true'
  showPronunciationBtn.value = localStorage.getItem('ssi-mode-pronunciation') === 'true'
}

/**
 * Closing the picker — and, for a SCOPED one, not being dropped somewhere else.
 *
 * The rule and the reasoning live in containers/scopedPickerClose; this is the
 * wiring. 'default', not 'chosen': we picked the dialect for the learner, and
 * a course we picked is not a choice (job #596).
 */
function closeCourseSelector() {
  const scoped = courseSelectorOnly.value
  const code = courseToFallBackTo(scoped, activeCourse.value?.course_code)
  showCourseSelector.value = false
  courseSelectorOnly.value = []
  if (!code) return
  const full = enrolledCourses?.value?.find(c => c.course_code === code)
  handleCourseSelect?.(full || { course_code: code, id: code }, 'default')
}

// A bar tap on a learner page off the player (job #46): the SAME bottom nav
// is mounted there, and it hands its tap here to be done exactly as it would
// have been in the player. Library and Settings need nothing loaded. The
// modes no longer come this way: they have addresses of their own (below).
const INTENT_PATIENCE_MS = 20000
const modeIntentWait = createModeIntentWait(INTENT_PATIENCE_MS)
const pendingModeIntent = modeIntentWait.pendingMode
const actOnPlayerIntent = (intent) => {
  if (!intent) return
  if (intent === 'library') { if (!showLibrary.value) toggleLibrary(); return }
  if (intent === 'settings') { if (!showSettings.value) toggleSettings(); return }
  if (intent === 'sector') { void openSectorPicker(sectorCourseCodeForPicker.value); return }
  if (intent === 'listening' || intent === 'script') { void router.replace({ path: modePath(intent), query: route.query }); return }
  if (intent === 'offline') modeIntentWait.run(null, () => !!learningPlayerRef.value && isPlayerReady.value, handleToggleOffline)
}
// Any mode tap while one is still waiting is the learner changing their mind.
const dropPendingIntent = modeIntentWait.drop

// LISTENING AND SCRIPT HAVE ADDRESSES (job #345; Tom 2026-09-26: "we already
// know which course we are in"). `/listening` and `/script` are this route
// with a mode in it, and the address and the open mode follow each other.
//
// A mode needs only what the app already holds — the course, the learner, and
// where they have reached — so it opens as soon as the player can say where
// that is (modeInputsReady), not after the Speaking screen has built its
// lesson. Until then, and while Listening's padlock is asked, the neutral
// cover stands (job #343) and the badge names the mode.
const routeMode = computed(() => routeModeOf(route.params.mode))
const playerMode = computed(() =>
  isListeningMode.value ? 'listening' : isScriptPracticeMode.value ? 'script' : null)
// A mode opened from the tray while the Library or Settings is up must not
// open underneath it (job #568): the sheets close whenever a mode opens.
useSheetsYieldToMode(playerMode, [showLibrary, showSettings])

const openRouteMode = (mode) => {
  if (mode === playerMode.value) { dropPendingIntent(); return }
  if (!mode) { handleSpeaking(); return }
  const ready = () => !!learningPlayerRef.value?.modeInputsReady
  const act = async () => {
    const p = learningPlayerRef.value
    if (!p || mode === playerMode.value) return
    // Listening is awaited: its padlock check runs before the switch, and the
    // cover must not lift onto Speaking in between. A course with no script
    // has nothing to open; the address goes back to Speaking below.
    if (mode === 'listening') await p.handleListeningToggle?.()
    else if (p.hasScriptPractice) p.handleScriptPracticeToggle?.()
  }
  modeIntentWait.run(mode, ready, act)
}
// Kept mounted behind a learner page (job #254, RoutedView): the address
// belongs to that page then, so the mode never follows it, nor it the mode.
const onPlayerRoute = () => route.name === 'player'
watch(routeMode, (mode) => { if (onPlayerRoute()) openRouteMode(mode) })

// The other direction: whatever mode is open once nothing is waiting is where
// the address points — a mode opened in the player, one refused by its
// padlock, one given up on, or a change of mind.
// It runs a task later and only if no navigation is under way: a bar tap
// that leaves the player closes the mode first, and moving the address then
// would cancel the tap's own navigation.
let leavingPlayer = false
let containerGone = false
const offBeforeEach = router.beforeEach((to) => { if (to.name !== 'player') leavingPlayer = true })
const offAfterEach = router.afterEach(() => { leavingPlayer = false })
onUnmounted(() => { containerGone = true; offBeforeEach(); offAfterEach(); stopInstalledReturn?.() })
// Stepping off to a learner page keeps this player mounted (job #254): it
// halts exactly as opening an overlay does, and Back finds it ready to play.
onDeactivated(() => { learningPlayerRef.value?.stopEverything?.() })
// A kept player is not mounted again, so a Library or Settings tap handed over
// from a learner page is taken here too (job #568). Left untaken, it fired on
// the next real mount, on top of whatever that visit opened.
onActivated(() => { actOnPlayerIntent(takePlayerIntent()) })
const followModeWithAddress = () => {
  if (leavingPlayer || containerGone || !onPlayerRoute() || pendingModeIntent.value) return
  const step = routeStepFor(routeMode.value, playerMode.value, window.history.state?.back)
  if (step.kind === 'back') router.back()
  else if (step.kind === 'push' || step.kind === 'replace') {
    void router[step.kind]({ path: step.path, query: route.query })
  }
}
watch([playerMode, pendingModeIntent], () => { setTimeout(followModeWithAddress, 0) })

onMounted(() => {
  // Installed app, signed out: the password sign-in opens itself (job #129).
  if (installedSignInPending.value) openAuth()
  actOnPlayerIntent(takePlayerIntent())
  if (routeMode.value) openRouteMode(routeMode.value)
  loadModeVisibility()
  loadAdaptationConsent()
  // Profile tiles and the encouragement taper, never the first word: they ask
  // after it (job #860, "one to 2 database lookups" at the start).
  void firstSound().then(() => {
    void loadEngagedMinutes()
    void loadLegosLearnt()
    // Only fetched when the mic/adaptation consent is on — the tile is hidden
    // otherwise, so for the default learner this is zero extra requests.
    if (adaptationConsented.value) void loadPhrasesSpoken()
  })

  // Pre-warm async modal chunks, after the first word (job #983). Idle with
  // a 2 s timeout fired mid-boot on a slow phone: about 100 kB of Settings,
  // Browse and Explorer shared the Fast-3G pipe with the course bundle. A
  // tap before the first sound still loads its chunk on click. Best-effort.
  const prewarm = () => {
    void import('@/components/SettingsScreen.vue').catch(() => {})
    void import('@/components/BrowseScreen.vue').catch(() => {})
    void import('@/components/CourseExplorer.vue').catch(() => {})
    void import('@/components/auth/SignInModal.vue').catch(() => {})
  }
  void firstSound().then(() => {
    void mountCourseSelector()
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(prewarm, { timeout: 2000 })
    } else {
      setTimeout(prewarm, 1500)
    }
  })

  // Listen for settings changes (from SettingsScreen toggle)
  window.addEventListener('ssi-setting-changed', (e) => {
    const { key } = e.detail || {}
    if (key?.startsWith('show') && key.endsWith('Mode')) loadModeVisibility()
    // Toggling the adaptation (mic) consent appears/disappears the spoken-
    // phrases tile in the Library without needing a reload.
    if (key === 'adaptationConsent') {
      loadAdaptationConsent()
      // Turning the mic on reveals the tile — fetch the lifetime number now
      // rather than showing the local 30-day fallback until the next reload.
      if (adaptationConsented.value) void loadPhrasesSpoken()
    }
  })

  // 'Or browse our free courses' on /premium pushes here with ?openCourses=1
  // — open the Choose Your Course modal directly. The modal (CourseSelector)
  // lives at the bottom of this container's template.
  //
  // The org-enrolment flow pushes the granted course codes instead of '1'
  // (?openCourses=cym_n_for_eng,cym_s_for_eng), and then the picker shows
  // those and nothing else: a Canolfan learner chooses their dialect, not
  // their language.
  const openCoursesParam = new URLSearchParams(window.location.search).get('openCourses')
  if (openCoursesParam) {
    if (openCoursesParam !== '1') {
      courseSelectorOnly.value = openCoursesParam.split(',').map(c => c.trim()).filter(Boolean)
    }
    showCourseSelector.value = true
    // Strip the param so a refresh doesn't keep re-opening the modal.
    router.replace({ path: '/', query: {} })
  }

  // Listen for mode tip "open settings" from LearningPlayer
  window.addEventListener('ssi-open-settings', () => {
    if (!showSettings.value) toggleSettings()
  })

  // Check if launched from Schools with class context
  const hasClassContext = checkClassContext()
  if (hasClassContext && classContext.value?.course_code) {
    // Switch to the class's course using full course metadata from enrolled list
    const courseCode = classContext.value.course_code
    if (handleCourseSelect && (!activeCourse.value || activeCourse.value.course_code !== courseCode)) {
      const fullCourse = enrolledCourses?.value?.find(c => c.course_code === courseCode)
      // An admin's class session never persists the class's course (job #601).
      if (classContext.value.adminLaunch) handleCourseSelect(fullCourse || { course_code: courseCode, id: courseCode }, 'chosen', { persist: false })
      else handleCourseSelect(fullCourse || { course_code: courseCode, id: courseCode })
    }
    currentScreen.value = 'player'
    isLearning.value = false
  }
})
</script>

<template>
  <div class="player-container" :class="{ 'has-nav': !isLearning, 'is-teach-embedded': isTeachEmbedded }" :style="containerBeltVars">
    <!-- Cultural journey backdrop (mist theme only, language-specific artwork) -->
    <SumiEBackground v-if="themeContext?.theme?.value === 'mist'" :lang="activeCourse?.target_lang" :belt-name="currentBeltName" :belt-color="currentBeltColor" />

    <!-- Learning Player - use v-show to keep it mounted when navigating away -->
    <LearningPlayer
      v-if="activeCourse"
      v-show="currentScreen === 'player'"
      :key="activeCourse?.course_code"
      ref="learningPlayerRef"
      :classContext="classContext"
      :embedded="isTeachEmbedded"
      :course="activeCourse"
      :previewLegoIndex="previewLegoIndex"
      :cumulativeLearningMinutes="serverEngagedMinutes"
      :isVisible="currentScreen === 'player'"
      @close="handleGoHome"
    />
    <!-- A mode picked off the player waits here for the course (job #343). -->
    <ModeCover v-if="pendingModeIntent && currentScreen === 'player'" />

    <!-- Player resting state overlay (shown when paused, hidden during playback).
         Gated on activeCourse only — the course identity (flag + name +
         subtitle) renders as soon as the Supabase row resolves. The belt
         badge and journey CTA inside PlayerRestingState have their own
         isPlayerReady gate, so the "White Belt" / globe placeholder issue
         we used to flash can't recur — the user sees the flag and language
         name immediately, then the belt and journey populate when the
         player has finished awakening. -->
    <!-- After a code sign-in on an account with no password (Aran, 2026-10-16). Resting screen only. -->
    <SetPasswordNudge
      v-if="currentScreen === 'player' && !showSettings && !isListeningMode && !isPronunciationMode && !isScriptPracticeMode && !isPlaying"
      @set-password="openSettingsOnPassword"
    />

    <!-- The way back to a speaking room (job #752): resting screen only. -->
    <SpeakingRoomChip
      v-if="!RELEASE_HOLDS.talkToHumans && activeCourse && currentScreen === 'player' && !isListeningMode && !isPronunciationMode && !isScriptPracticeMode && !isPlaying"
      :course-target-lang="activeCourse?.target_lang"
    />

    <PlayerRestingState
      v-if="activeCourse && currentScreen === 'player' && !isListeningMode && !isPronunciationMode && !isScriptPracticeMode && !isPlaying"
      :course="activeCourse"
      :completed-seeds="completedSeeds"
      :total-seeds="totalSeeds"
      :current-belt-name="currentBeltName"
      :is-player-ready="isPlayerReady"
      :learning-mode="learningPlayerRef?.learningMode ?? 'fast'"
      @start="handleTogglePlayback"
      @change-course="showCourseSelector = true"
      :show-mode-switch-reminder="showModeSwitchReminder"
      @set-learning-mode="handleSetLearningMode"
      @mode-switch-reminder-seen="handleModeSwitchReminderSeen"
    />

    <!-- Library overlay (slide-up modal, same pattern as Settings) -->
    <Transition name="slide-up">
      <div v-if="showLibrary" class="settings-overlay" @click.self="closeLibrary">
        <div class="settings-panel">
          <BrowseScreen
            :active-course="activeCourse"
            :enrolled-courses="enrolledCourses"
            :completed-seeds="completedSeeds"
            :total-seeds="totalSeeds"
            :current-belt-name="currentBeltName"
            :total-learning-minutes="totalLearningMinutes"
            :total-learning-minutes-estimated="totalLearningMinutesEstimated"
            :total-phrases-spoken="totalPhrasesSpoken"
            :show-phrases-spoken="showPhrasesSpokenTile"
            :phrases-learnt="phrasesLearnt"
            @open-belts="null"
            @select-course="(c) => { closeLibrary(); handleCourseSelect(c) }"
            @close="closeLibrary"
            @start-seed="handleStartAtSeed"
            @open-listening-all="handleOpenListeningAll"
            @open-journey="handleOpenJourney"
            :is-offline-mode="learningPlayerRef?.offlineActive ?? false"
            :has-sector-thread="!!activeSectorThread"
            :is-sector-active="!!activeSectorThread?.active"
            :sector-desc="sectorDesc"
            @toggle-offline="handleLibraryOffline"
            @open-sector="handleLibraryOpenSector"
            @toggle-sector="handleSectorToggle"
          />
        </div>
      </div>
    </Transition>

    <!-- Bottom Navigation -->
    <BottomNav
      :currentScreen="currentScreen"
      :isLearning="isLearning"
      :isPlaying="isPlaying"
      :isListeningMode="isListeningMode"
      :isPronunciationMode="isPronunciationMode"
      :showLibrary="showLibrary"
      :showSettings="showSettings"
      :isAuthOpen="isAuthOpen"
      :showCourseSelector="showCourseSelector"
      :hasScriptPractice="playerHasScriptPractice"
      :isScriptPracticeMode="isScriptPracticeMode"
      :isPlayerReady="isPlayButtonReady"
      :showListeningBtn="showListeningBtn"
      :showPronunciationBtn="showPronunciationBtn"
      :courseCode="sectorCourseCodeForPicker"
      :isInListeningCycle="learningPlayerRef?.isInListeningCycle ?? false"
      :showConnect="showConnect"
      :pageMode="pendingModeIntent"
      @navigate="handleNavigation"
      @startLearning="handleStartLearning"
      @togglePlayback="handleTogglePlayback"
      @exitListeningMode="handleExitListeningMode"
      @backListening="handleBackListening"
      @exitPronunciationMode="handleExitPronunciationMode"
      @exitScriptPracticeMode="handleExitScriptPracticeMode"
      @toggleListening="handleToggleListening"
      @togglePronunciation="handleTogglePronunciation"
      @toggleScriptPractice="handleToggleScriptPractice"
      @speaking="handleSpeaking"
      @talk="handleTalk"
      @studyGroups="handleStudyGroups"
      @revisit="handleRevisit"
      @skip="handleSkip"
      @openSettings="toggleSettings"
      @closeOverlays="closeLibrary(); closeSettings(); showCourseSelector = false"
      @closeAuth="closeAuth"
    />

    <!-- Sector walk + role picker. Body-teleported inside the component, the
         way the offline depth picker is: one popup at a time, and the tray has
         already closed itself by the time this opens. -->
    <SectorPicker
      :open="showSectorPicker"
      :sectors="sectorOptions"
      :loading="loadingSectors"
      :error="sectorsError"
      :coreHighestLegoId="beltProgress?.highestLegoId?.value ?? null"
      :currentSectorCourseCode="activeSectorThread?.sectorCourseCode ?? null"
      :currentRole="activeSectorThread?.role ?? null"
      @close="showSectorPicker = false"
      @retry="openSectorPicker(sectorCourseCodeForPicker)"
      @choose="handleSectorChoose"
    />

    <!-- Gear icon removed — settings now accessible from bottom pill -->

    <!-- Settings overlay (slide-up modal) -->
    <Transition name="slide-up">
      <div v-if="showSettings" class="settings-overlay" @click.self="closeSettings">
        <div class="settings-panel">
          <SettingsScreen
            :course="activeCourse"
            :openPasswordForm="settingsOpenPassword"
            @close="closeSettings"
            @openExplorer="openExplorerOverlay"
          />
        </div>
      </div>
    </Transition>

    <!-- Course Explorer overlay (nested inside settings flow) -->
    <Transition name="slide-up">
      <div v-if="showExplorer" class="settings-overlay" @click.self="closeExplorerOverlay">
        <div class="settings-panel">
          <CourseExplorer
            :course="activeCourse"
            @close="closeExplorerOverlay"
          />
        </div>
      </div>
    </Transition>

    <!-- Course Selector (mounted once asked for or after the first sound, manages own overlay) -->
    <CourseSelector
      v-if="courseSelectorMounted"
      :is-open="courseSelectorOpen"
      :only-courses="courseSelectorOnly"
      :supabase="supabaseClient"
      :enrolled-courses="enrolledCourses"
      :active-course-id="activeCourse?.course_code"
      :is-admin="isAdmin"
      @selectCourse="(c) => { showCourseSelector = false; courseSelectorOnly = []; handleCourseSelect(c) }"
      @close="closeCourseSelector()"
    />

    <!-- Unified Auth Modal (shared state with all components) -->
    <SignInModal @success="handleAuthSuccess" />

  </div>
</template>

<style scoped>
.player-container {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg-primary);
}

/* Add bottom padding when nav is visible */
.player-container.has-nav {
  padding-bottom: var(--nav-height-safe);
}

/* Embedded in the teach shell ("Play as class"): the TopNav stays fixed at the
   top. `transform` makes this element the containing block for its `position:
   fixed` descendants (the player root + every pop-up), so they anchor to this
   box instead of the viewport. Sized to the area BELOW the nav (the parent
   .main-content already offsets us down by the nav height), so nothing — modal
   close buttons, mode-tray controls, the player header — hides under the nav. */
.player-container.is-teach-embedded {
  transform: translateZ(0);
  min-height: 0;
  height: calc(100vh - var(--nav-height) - env(safe-area-inset-top, 0px));
  height: calc(100dvh - var(--nav-height) - env(safe-area-inset-top, 0px));
}

/* Slide up transition for overlays */
.slide-up-enter-active {
  transition: opacity 0.3s ease;
}

.slide-up-enter-active .settings-panel {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-leave-active {
  transition: opacity 0.25s ease-in;
}

.slide-up-leave-active .settings-panel {
  transition: transform 0.2s ease-in;
}

.slide-up-enter-from {
  opacity: 0;
}

.slide-up-enter-from .settings-panel {
  transform: translateY(100%);
}

.slide-up-leave-to {
  opacity: 0;
}

.slide-up-leave-to .settings-panel {
  transform: translateY(100%);
}

/* Settings overlay */
.settings-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.settings-panel {
  width: 100%;
  max-width: 500px;
  /* Safety net: with the async modals (Settings, Library, Explorer,
     SignIn), the panel content is briefly empty while the chunk is
     in flight. Without a min-height the panel collapses to 0 and
     the user sees only the scrim — confusing. Pre-warm normally
     beats them to it, but this guarantees a visible panel even on
     a cold click. */
  min-height: 50vh;
  max-height: 85dvh;
  overflow-y: auto;
  background: var(--bg-primary);
  border-radius: 16px 16px 0 0;
  overscroll-behavior: contain;
}

@media (min-width: 540px) {
  .settings-panel {
    border-left: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.06));
    border-right: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.06));
  }
}

@media (display-mode: standalone) {
  .settings-panel {
    max-height: 90vh;
  }
}


</style>

<!-- Mist theme: subtle border for desktop panels -->
<style>
:root[data-theme="mist"] .player-container .settings-panel {
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

@media (max-width: 539px) {
  :root[data-theme="mist"] .player-container .settings-panel {
    border-left: none;
    border-right: none;
  }
}
</style>
